import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { useLeadDadosComplementar, type Sociedade } from '@/hooks/useLeadDadosComplementar';
import { formatarTelefone, formatarCPF, formatarMoeda } from '@/utils/formatters';
import {
  Phone, Mail, MapPin, Building2, Users, AlertCircle,
  MessageSquare, PhoneCall, Database, ChevronDown, ChevronRight,
  Briefcase, Globe, Shield, UserPlus
} from 'lucide-react';
import { CopyOnClick } from '@/components/planilha/CopyOnClick';
import { cn } from '@/lib/utils';

interface LeadDadosComplementarTabProps {
  cnpj: string;
  onLigar?: (telefone: string) => void;
}

// Campo compacto label/valor
function Field({ label, value, mono }: { label: string; value?: string | number | null; mono?: boolean }) {
  if (!value && value !== 0) return null;
  return (
    <div className="min-w-0">
      <span className="text-[10px] text-muted-foreground uppercase tracking-wide block leading-tight">{label}</span>
      <span className={`text-sm leading-tight block truncate ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

function TelButton({ tel, op, wpp, onLigar }: { tel?: string; op?: string; wpp?: boolean; onLigar?: (t: string) => void }) {
  if (!tel) return null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <Button variant="ghost" size="sm" className="h-7 px-1.5 font-mono text-xs" onClick={() => onLigar?.(tel)}>
        <PhoneCall className="w-3 h-3 mr-1" />
        {formatarTelefone(tel)}
      </Button>
      {op && <span className="text-[10px] text-muted-foreground">{op}</span>}
      {wpp && (
        <Badge variant="outline" className="h-4 text-[10px] px-1 bg-success/5 text-success border-success/40">
          <MessageSquare className="w-2.5 h-2.5 mr-0.5" />WA
        </Badge>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, defaultOpen = true, children, badge }: {
  icon: React.ElementType; title: string; defaultOpen?: boolean; children: React.ReactNode; badge?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center gap-2 w-full py-1.5 px-2 hover:bg-muted/50 rounded-md transition-colors">
        {open ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold uppercase tracking-wide">{title}</span>
        {badge && <Badge variant="secondary" className="h-4 text-[10px] px-1.5 ml-auto">{badge}</Badge>}
      </CollapsibleTrigger>
      <CollapsibleContent className="px-2 pb-3 pt-1">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

const LABEL_FONTE: Record<string, string> = {
  lead: 'Lead', lead_contato: 'Lead', complementar: 'Complementar',
  contato_master: 'Contato', contato_master_whats: 'WhatsApp', gestor: 'Gestor', cliente: 'Cliente',
  assertiva_cnpj: 'Assertiva', assertiva_empresa: 'Assertiva', assertiva_socio: 'Sócio (Assertiva)',
};

// Painel "Emails encontrados" — TODAS as fontes (lead, complementar, Assertiva
// empresa + sócios). Antes o email do sócio existia mas não aparecia no card.
function EmailsTodasFontes({ cnpj }: { cnpj: string }) {
  const { data: emails } = useQuery({
    queryKey: ['emails-do-cnpj', cnpj],
    enabled: !!cnpj,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('emails_do_cnpj' as any, { p_cnpj: cnpj });
      if (error) throw error;
      return (data || []) as Array<{ email: string; fontes: string[]; prioridade: number }>;
    },
  });
  if (!emails || emails.length === 0) return null;
  return (
    <>
      <div className="border-t border-border/40" />
      <Section icon={Mail} title="Emails encontrados" defaultOpen badge={String(emails.length)}>
        <div className="flex flex-col gap-1.5">
          {emails.map((e) => (
            <div key={e.email} className="flex items-center gap-2 flex-wrap">
              <a href={`mailto:${e.email}`} className="text-sm font-mono text-primary hover:underline break-all">{e.email}</a>
              {Array.from(new Set((e.fontes || []).map((f) => LABEL_FONTE[f] || f))).map((lbl) => (
                <Badge key={lbl} variant="outline" className="h-4 text-[10px] px-1">{lbl}</Badge>
              ))}
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

// Painel "Telefones encontrados" — TODAS as fontes (lead, complementar, contato,
// Assertiva empresa + sócios). 62a: virou o PRIMEIRO card da aba, fonte-neutra —
// o chip de origem é quem diz a procedência (2 dos 7 números só existem no
// Complementar; dentro da Assertiva eles sumiriam).
export function TelefonesEncontradosCard({ cnpj, onLigar, onWhatsApp, onUsarNoContato }: {
  cnpj: string;
  onLigar?: (t: string) => void;
  onWhatsApp?: (t: string) => void;
  onUsarNoContato?: (t: string) => void;
}) {
  const { data: telefones } = useQuery({
    queryKey: ['telefones-do-cnpj', cnpj],
    enabled: !!cnpj,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('telefones_do_cnpj' as any, { p_cnpj: cnpj });
      if (error) throw error;
      return (data || []) as Array<{ telefone: string; fontes: string[]; prioridade: number; whatsapp: boolean }>;
    },
  });
  if (!telefones || telefones.length === 0) return null;
  return (
    <div className="relative overflow-hidden rounded-xl border border-border bg-card pl-1">
      <span aria-hidden className="absolute inset-y-0 left-0 w-1 bg-[linear-gradient(180deg,#6E1626,#4E1522_55%,#3E0E1B)]" />
      <div className="flex h-[38px] items-center justify-between gap-3 border-b border-border px-4">
        <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-foreground">
          <Phone className="h-3.5 w-3.5 text-muted-foreground" />Telefones encontrados
          <span className="font-semibold normal-case tracking-normal text-muted-foreground">· todas as fontes · {telefones.length} números</span>
        </p>
      </div>
      <div>
        {/* doc telefones-encontrados: linha 33px com zebra; TELEFONE 136px 12/700
            tabular + ⧉ · ORIGENS elástica (chips ficam — 1 número tem N fontes) ·
            AÇÕES = WhatsApp SÓLIDO (a mais usada) + 2 ghosts só-ícone com title */}
        {telefones.map((t, index) => (
          <div
            key={t.telefone}
            className={cn(
              'grid box-border h-[33px] grid-cols-[136px_minmax(0,1fr)_86px] items-center gap-2 overflow-hidden border-b border-border px-4 last:border-b-0 hover:bg-[#F4F0EA] dark:hover:bg-white/[0.04]',
              index % 2 ? 'bg-[#FBF9F6] dark:bg-white/[0.02]' : 'bg-card',
            )}
          >
            <span className="flex items-center gap-1.5">
              <CopyOnClick value={t.telefone}>
                <span className="cursor-pointer whitespace-nowrap text-[12px] font-bold tabular-nums text-card-foreground hover:underline" title={`${formatarTelefone(t.telefone)} — clique copia`}>
                  {formatarTelefone(t.telefone)}
                </span>
              </CopyOnClick>
            </span>
            <span className="flex min-w-0 items-center gap-1.5 overflow-hidden">
              {Array.from(new Set((t.fontes || []).map((f) => LABEL_FONTE[f] || f))).map((lbl) => (
                <span key={lbl} className="flex h-[18px] flex-shrink-0 items-center rounded-full bg-[#F2EEE8] px-2 text-[9px] font-bold uppercase tracking-wider text-muted-foreground dark:bg-white/[0.06]">{lbl}</span>
              ))}
              {t.whatsapp && <span className="flex h-[18px] flex-shrink-0 items-center rounded-full bg-[#F2EEE8] px-2 text-[9px] font-bold uppercase tracking-wider text-[#075E54] dark:bg-white/[0.06] dark:text-[#25D366]">WhatsApp</span>}
            </span>
            <div className="flex flex-shrink-0 items-center justify-end gap-1">
              {onWhatsApp && (
                <button
                  type="button"
                  className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-[7px] bg-[linear-gradient(135deg,#4A1320,#5B0A0D)] text-white shadow-[0_3px_8px_-4px_rgba(91,10,13,0.6)] transition-all hover:bg-[linear-gradient(135deg,#5B0A0D,#6E1626)] hover:brightness-110 dark:bg-[linear-gradient(135deg,#9D2530,#6C2236)]"
                  title="Abrir WhatsApp"
                  onClick={() => onWhatsApp(t.telefone)}
                >
                  <MessageSquare className="h-[11px] w-[11px]" />
                </button>
              )}
              <button
                type="button"
                className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-[7px] border border-[#E4DDD3] bg-[#FAF8F5] text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary dark:border-border dark:bg-white/[0.04]"
                title="Ligar pela discadora"
                onClick={() => onLigar?.(t.telefone)}
              >
                <PhoneCall className="h-[11px] w-[11px]" />
              </button>
              {onUsarNoContato && (
                <button
                  type="button"
                  className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-[7px] border border-[#E4DDD3] bg-[#FAF8F5] text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary dark:border-border dark:bg-white/[0.04]"
                  title="Usar no contato — preenche o telefone principal"
                  onClick={() => onUsarNoContato(t.telefone)}
                >
                  <UserPlus className="h-[11px] w-[11px]" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* rodapé do card: a nota da origem desceu para cá */}
      <div className="flex h-7 items-center border-t border-border px-4 text-[10px] text-muted-foreground">
        a origem de cada número está no chip
      </div>
    </div>
  );
}

function TelefonesTodasFontes({ cnpj, onLigar }: { cnpj: string; onLigar?: (t: string) => void }) {
  const { data: telefones } = useQuery({
    queryKey: ['telefones-do-cnpj', cnpj],
    enabled: !!cnpj,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('telefones_do_cnpj' as any, { p_cnpj: cnpj });
      if (error) throw error;
      return (data || []) as Array<{ telefone: string; fontes: string[]; prioridade: number; whatsapp: boolean }>;
    },
  });
  if (!telefones || telefones.length === 0) return null;
  return (
    <>
      <div className="border-t border-border/40" />
      <Section icon={Phone} title="Telefones encontrados" defaultOpen badge={String(telefones.length)}>
        <div className="flex flex-col gap-1.5">
          {telefones.map((t) => (
            <div key={t.telefone} className="flex items-center gap-2 flex-wrap">
              <TelButton tel={t.telefone} wpp={t.whatsapp} onLigar={onLigar} />
              {Array.from(new Set((t.fontes || []).map((f) => LABEL_FONTE[f] || f))).map((lbl) => (
                <Badge key={lbl} variant="outline" className="h-4 text-[10px] px-1">{lbl}</Badge>
              ))}
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

export function LeadDadosComplementarTab({ cnpj, onLigar }: LeadDadosComplementarTabProps) {
  const { data, isLoading, error } = useLeadDadosComplementar(cnpj);

  if (isLoading) return <div className="space-y-3"><Skeleton className="h-24 w-full" /><Skeleton className="h-24 w-full" /></div>;
  if (error) return <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>Erro ao carregar dados complementares</AlertDescription></Alert>;
  if (!data) return <p className="text-xs text-muted-foreground text-center py-3">Dados complementares não disponíveis.</p>;

  const sociedades: Sociedade[] = Array.isArray(data.sociedades) ? data.sociedades : [];

  const hasSocio = (n: number) => {
    if (n === 1) return !!(data.nome_socio1 || data.cpf_socio1);
    if (n === 2) return !!(data.nome_socio2 || data.cpf_socio2);
    if (n === 3) return !!(data.nome_socio3 || data.cpf_socio3);
    if (n === 4) return !!(data.nome_socio4);
    return false;
  };

  return (
    <div className="space-y-1 text-sm">
      {/* ═══ 62a — DADOS CREDLINK: grid de pares, — para o que a fonte não retornou ═══ */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 px-2 py-2 md:grid-cols-4">
        {[
          ['Razão social', data.razao_social || '—'],
          ['Tipo de empresa', data.tipo_empresa || '—'],
          ['Setor público', data.setor_publico || '—'],
          ['Empresa individual', data.empresa_individual || '—'],
          ['Classificação', data.classificacao || '—'],
          ['Aptidão', data.aptidao || '—'],
        ].map(([rotulo, valor]) => (
          <div key={rotulo} className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">{rotulo}</p>
            <p className="mt-0.5 truncate text-sm font-semibold text-foreground" title={String(valor)}>{valor}</p>
          </div>
        ))}
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Situação cadastral</p>
          {data.situacao_cadastral ? (
            <span className={`mt-0.5 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${String(data.situacao_cadastral).normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase().startsWith('ATIV') ? 'border-[#075E54]/40 bg-[#075E54]/[0.06] text-[#075E54] dark:text-[#25D366]' : 'border-primary/40 bg-primary/5 text-primary dark:text-[#C87484]'}`}>
              {data.situacao_cadastral.toUpperCase()}
            </span>
          ) : <p className="mt-0.5 text-sm font-semibold text-foreground">—</p>}
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Faturamento presumido</p>
          <p className="mt-0.5 truncate font-display text-[17px] font-bold tabular-nums text-money-highlight">{data.faturamento_presumido ? formatarMoeda(data.faturamento_presumido) : '—'}</p>
        </div>
      </div>
      <p className="px-2 pb-1 text-[10px] text-muted-foreground">campos sem retorno da fonte mostram — · o faturamento presumido alimenta a análise de crédito{data.data_enriquecimento ? ` · enriquecido ${data.data_enriquecimento}` : ''}</p>
      <Section icon={Briefcase} title="Mais do cadastro" defaultOpen={false}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
          <Field label="Nome Fantasia" value={data.fantasia} />
          <Field label="Consultor" value={data.consultor_nome} />
          <Field label="Território" value={data.territorio} />
          <Field label="Linhas" value={data.linhas} />
          <Field label="Meses Contrato" value={data.meses_contrato} />
          <Field label="Tel. Sistema" value={data.telefone_sistema ? formatarTelefone(data.telefone_sistema) : undefined} mono />
        </div>
      </Section>

      {/* ═══ EMAILS (TODAS AS FONTES: lead + complementar + Assertiva empresa/sócios) ═══ */}
      <TelefonesTodasFontes cnpj={cnpj} onLigar={onLigar} />
      <EmailsTodasFontes cnpj={cnpj} />

      <div className="border-t border-border/40" />

      {/* ═══ TELEFONES E CONTATOS ═══ */}
      <Section icon={Phone} title="Telefones e Contatos" defaultOpen={true}>
        <div className="space-y-2">
          {/* Fixos */}
          {(data.fixo1 || data.fixo2 || data.fixo3 || data.fixo4) && (
            <div>
              <span className="text-[10px] text-muted-foreground uppercase block mb-0.5">Fixos</span>
              <div className="flex flex-wrap gap-1">
                <TelButton tel={data.fixo1} op={data.operadora_fixo1} onLigar={onLigar} />
                <TelButton tel={data.fixo2} op={data.operadora_fixo2} onLigar={onLigar} />
                <TelButton tel={data.fixo3} op={data.operadora_fixo3} onLigar={onLigar} />
                <TelButton tel={data.fixo4} op={data.operadora_fixo4} onLigar={onLigar} />
              </div>
            </div>
          )}
          {/* Celulares */}
          {(data.celular1 || data.celular2 || data.celular3 || data.celular4) && (
            <div>
              <span className="text-[10px] text-muted-foreground uppercase block mb-0.5">Celulares</span>
              <div className="flex flex-wrap gap-1">
                <TelButton tel={data.celular1} op={data.operadora_celular1} wpp={data.whatsapp1} onLigar={onLigar} />
                <TelButton tel={data.celular2} op={data.operadora_celular2} wpp={data.whatsapp2} onLigar={onLigar} />
                <TelButton tel={data.celular3} op={data.operadora_celular3} wpp={data.whatsapp3} onLigar={onLigar} />
                <TelButton tel={data.celular4} op={data.operadora_celular4} wpp={data.whatsapp4} onLigar={onLigar} />
              </div>
            </div>
          )}
          {/* Emails */}
          {(data.email1 || data.email2 || data.email3) && (
            <div>
              <span className="text-[10px] text-muted-foreground uppercase block mb-0.5">E-mails</span>
              <div className="flex flex-wrap gap-3 text-xs">
                {data.email1 && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{data.email1}</span>}
                {data.email2 && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{data.email2}</span>}
                {data.email3 && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{data.email3}</span>}
              </div>
            </div>
          )}
          {!data.fixo1 && !data.fixo2 && !data.fixo3 && !data.fixo4 && !data.celular1 && !data.celular2 && !data.celular3 && !data.celular4 && !data.email1 && (
            <span className="text-muted-foreground text-xs">Nenhum contato registrado</span>
          )}
        </div>
      </Section>

      <div className="border-t border-border/40" />

      {/* ═══ DADOS EMPRESARIAIS ═══ */}
      <Section icon={Building2} title="Dados Empresariais" defaultOpen={true}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
          <Field label="CNAE" value={data.cod_cnae ? `${data.cod_cnae}` : undefined} />
          <div className="col-span-2 md:col-span-2">
            <Field label="Descrição CNAE" value={data.descricao_cnae} />
          </div>
          <Field label="Capital Social" value={data.capital_social ? formatarMoeda(data.capital_social) : undefined} />
          <Field label="Porte" value={data.porte_empresa} />
          <Field label="Funcionários" value={data.qtd_funcionario} />
          <Field label="Porte Funcionários" value={data.porte_funcionarios} />
          <Field label="Natureza" value={data.natureza_empresa} />
          <Field label="Descrição Natureza" value={data.descricao_natureza} />
          <Field label="Tipo Filial" value={data.tipo_filial} />
          <Field label="Nome ADM" value={data.nome_adm} />
          <Field label="Data Abertura" value={data.data_abertura} />
          <Field label="Data Emissão" value={data.data_emissao} />
          <Field label="Data Situação" value={data.data_situacao} />
          <Field label="Motivo Situação" value={data.motivo_situacao_cadastral} />
          <Field label="Situação Especial" value={data.situacao_especial} />
          <Field label="Data Sit. Especial" value={data.data_situacao_especial} />
          <Field label="Qtde Sócios" value={data.qtde_socios} />
          <div>
            <span className="text-[10px] text-muted-foreground uppercase tracking-wide block leading-tight">Restrição Bancária</span>
            {data.restricao_bancaria !== undefined && data.restricao_bancaria !== null ? (
              data.restricao_bancaria ? (
                <Badge variant="destructive" className="text-[10px] h-5">Sim</Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] h-5 bg-success/5 text-success border-success/40">Não</Badge>
              )
            ) : <span className="text-xs text-muted-foreground">-</span>}
          </div>
        </div>
      </Section>

      <div className="border-t border-border/40" />

      {/* ═══ ENDEREÇO ═══ */}
      {data.endereco && (
        <>
          <Section icon={MapPin} title="Endereço" defaultOpen={false}>
            <div className="text-xs space-y-0.5">
              <p>
                {data.endereco}{data.numero ? `, ${data.numero}` : ''}
                {data.complemento ? ` - ${data.complemento}` : ''}
              </p>
              <p className="text-muted-foreground">
                {data.bairro && `${data.bairro} - `}{data.cidade}/{data.uf} {data.cep ? `- CEP: ${data.cep}` : ''}
              </p>
            </div>
          </Section>
          <div className="border-t border-border/40" />
        </>
      )}

      {/* ═══ SÓCIOS 1-4 ═══ */}
      {[1, 2, 3, 4].map(n => {
        if (!hasSocio(n)) return null;
        const s = n === 1 ? {
          cpf: data.cpf_socio1, nome: data.nome_socio1, cargo: data.cargo_socio1,
          end: data.endereco_socio1, num: data.numero_socio1, comp: data.complemento_socio1,
          bairro: data.bairro_socio1, cidade: data.cidade_socio1, uf: data.uf_socio1, cep: data.cep_socio1,
          tel1: data.telefone1_socio1, tel2: data.telefone2_socio1, tel3: data.telefone3_socio1,
          part: data.participacao_socio1, sit: data.situacao_socio1, cnpjS: data.cnpj_socio1,
        } : n === 2 ? {
          cpf: data.cpf_socio2, nome: data.nome_socio2, cargo: data.cargo_socio2,
          end: data.endereco_socio2, num: data.numero_socio2, comp: data.complemento_socio2,
          bairro: data.bairro_socio2, cidade: data.cidade_socio2, uf: data.uf_socio2, cep: data.cep_socio2,
          tel1: data.telefone1_socio2, tel2: data.telefone2_socio2, tel3: data.telefone3_socio2,
          part: data.participacao_socio2, sit: data.situacao_socio2, cnpjS: data.cnpj_socio2,
        } : n === 3 ? {
          cpf: data.cpf_socio3, nome: data.nome_socio3, cargo: data.cargo_socio3,
          end: data.endereco_socio3, num: data.numero_socio3, comp: data.complemento_socio3,
          bairro: data.bairro_socio3, cidade: data.cidade_socio3, uf: data.uf_socio3, cep: data.cep_socio3,
          tel1: data.telefone1_socio3, tel2: data.telefone2_socio3, tel3: data.telefone3_socio3,
          part: data.participacao_socio3, sit: data.situacao_socio3, cnpjS: data.cnpj_socio3,
        } : {
          cpf: undefined, nome: data.nome_socio4, cargo: data.cargo_socio4,
          end: undefined, num: undefined, comp: undefined,
          bairro: undefined, cidade: undefined, uf: undefined, cep: undefined,
          tel1: data.telefone1_socio4, tel2: undefined, tel3: undefined,
          part: undefined, sit: undefined, cnpjS: undefined,
        };

        return (
          <div key={n}>
            <Section icon={Users} title={`Sócio ${n}`} defaultOpen={n === 1} badge={s.cargo || undefined}>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5">
                <Field label="Nome" value={s.nome} />
                <Field label="CPF" value={s.cpf ? formatarCPF(s.cpf) : undefined} mono />
                <Field label="Cargo" value={s.cargo} />
                <Field label="CNPJ Sociedade" value={s.cnpjS} mono />
                <Field label="Participação" value={s.part} />
                <Field label="Situação" value={s.sit} />
              </div>
              {(s.tel1 || s.tel2 || s.tel3) && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  <TelButton tel={s.tel1} wpp={n === 1 ? data.whatsapp_socio1 : n === 2 ? data.whatsapp_socio2 : n === 3 ? data.whatsapp_socio3 : data.whatsapp_socio4} onLigar={onLigar} />
                  <TelButton tel={s.tel2} onLigar={onLigar} />
                  <TelButton tel={s.tel3} onLigar={onLigar} />
                </div>
              )}
              {s.end && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  {s.end}{s.num ? `, ${s.num}` : ''}{s.comp ? ` - ${s.comp}` : ''}, {s.bairro} - {s.cidade}/{s.uf} {s.cep}
                </p>
              )}
            </Section>
            <div className="border-t border-border/40" />
          </div>
        );
      })}

      {/* ═══ SOCIEDADES (JSONB) ═══ */}
      {sociedades.length > 0 && (
        <Section icon={Globe} title={`Sociedades (${sociedades.length})`} defaultOpen={false}>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="text-left py-1 pr-2">#</th>
                  <th className="text-left py-1 pr-2">CPF/CNPJ</th>
                  <th className="text-left py-1 pr-2">Razão Social</th>
                  <th className="text-left py-1 pr-2">Participação</th>
                  <th className="text-left py-1">Situação</th>
                </tr>
              </thead>
              <tbody>
                {sociedades.map((s, i) => (
                  <tr key={i} className="border-b border-border/30">
                    <td className="py-1 pr-2 text-muted-foreground">{s.numero || i + 1}</td>
                    <td className="py-1 pr-2 font-mono">{s.cpf || s.cnpj || '-'}</td>
                    <td className="py-1 pr-2">{s.razao_social || '-'}</td>
                    <td className="py-1 pr-2">{s.participacao || '-'}</td>
                    <td className="py-1">{s.situacao || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
}
